import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The private Find beacon (docs/beacon-privacy.md, 2026-10-16): native advertises a 15-minute rotating id and seals
 * its GPS with day keys only your Find audience holds, instead of "Lynx|<user id>" and plain coordinates.
 *
 * - 'shadow' (default): the legacy beacon is untouched; the private id and sealed GPS ride along in two extra
 *   characteristics, and each friend's are read and checked against the legacy truth - results go to lynx_find_logs
 *   (find.native.beacon.shadow_*). Nothing a user sees changes; it tells us whether private mode would have worked.
 * - 'on': the private beacon replaces the legacy one. Only after the shadow numbers say so.
 * - 'off': neither.
 *
 * Build: VITE_BEACON_PRIVACY=off|shadow|on (1 = on). One phone: localStorage['prii:beaconPrivacy'] = 'off'|'shadow'|'on'
 * (or '0' / '1').
 */
export type BeaconMode = 'off' | 'shadow' | 'on';

function parseMode(value: string | null | undefined): BeaconMode | null {
  if (value === '1' || value === 'on') return 'on';
  if (value === '0' || value === 'off') return 'off';
  if (value === 'shadow') return 'shadow';
  return null;
}

export function getBeaconMode(): BeaconMode {
  try {
    if (typeof localStorage !== 'undefined') {
      const local = parseMode(localStorage.getItem('prii:beaconPrivacy'));
      if (local) return local;
    }
  } catch {
    // Storage blocked: fall through to the build flag.
  }
  const meta = import.meta as ImportMeta & { env?: { VITE_BEACON_PRIVACY?: string } };
  return parseMode(meta.env?.VITE_BEACON_PRIVACY) ?? 'shadow';
}

export function isBeaconPrivacyOn(): boolean {
  return getBeaconMode() === 'on';
}

const MODE_EVENT = 'prii:beacon-mode';

/** Settings' Find testing card (operators only): saved on this phone; a running Find session restarts with it. */
export function setBeaconMode(mode: BeaconMode): void {
  try {
    localStorage.setItem('prii:beaconPrivacy', mode);
  } catch {
    return;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: mode }));
  }
}

export function onBeaconModeChange(cb: (mode: BeaconMode) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => cb(getBeaconMode());
  window.addEventListener(MODE_EVENT, handler);
  return () => window.removeEventListener(MODE_EVENT, handler);
}

export type BeaconKeyRow = { userId: string; day: string; key: string };

/**
 * This event's day keys for you and everyone your Find watches (lynx_beacon_keys, migration 155). Null on any failure:
 * native keeps the keys it already has, which is what an offline phone runs on.
 */
export async function fetchBeaconKeys(
  client: SupabaseClient,
  eventId: string,
): Promise<BeaconKeyRow[] | null> {
  const { data, error } = await client.rpc('lynx_beacon_keys', { p_event_id: eventId });
  if (error || !Array.isArray(data)) {
    return null;
  }
  return data
    .map((row: { user_id?: unknown; day?: unknown; key?: unknown }) => ({
      userId: String(row.user_id ?? ''),
      day: String(row.day ?? '').slice(0, 10),
      key: String(row.key ?? '').replace(/\s+/g, ''),
    }))
    .filter((row) => row.userId && /^\d{4}-\d{2}-\d{2}$/.test(row.day) && row.key);
}
